// BuildLog: streaming build logs rendered in CodeMirror with ANSI coloring.
// Receives lines via LiveView push_event ("buildLogLine") — no template
// diffs, no DOM fights with the DAG chart. Persisted logs loaded via
// "buildLogLoad" on job select / attempt switch / job completion.

import type { LiveViewHook, CiPushEventWire } from "../types";
import {
  initBuildLogViewer,
  appendBuildLogText,
  setBuildLogContent,
  scrollBuildLogToEnd,
  destroyBuildLogViewer,
  isBuildLogAutoScroll,
} from "../../codemirror";
// Build-log toolbar button ids are dox-owned (`CiElementId` in
// `ui/ci.dox`) so a wire-string rename upstream surfaces here as a
// tsc error instead of a silent `null` from `getElementById`.
import { validCiElementId } from "../../../../.dox/ui";

type BuildLogHook = LiveViewHook<CiPushEventWire> & {
  _pendingLines?: string[];
  _rafId?: number;
  _scrollDom?: Element;
  _scrollHandler?: () => void;
  _progressBar?: HTMLDivElement;
  _progressRaf?: number;
  _fullscreenActive?: boolean;
  _escHandler?: (e: KeyboardEvent) => void;
};

const ensureProgressBar = (hook: BuildLogHook): HTMLDivElement => {
  const existing = hook._progressBar;
  if (existing) return existing;

  if (!(hook.el instanceof HTMLElement)) {
    throw new Error("BuildLog hook element is not an HTMLElement");
  }

  const bar = document.createElement("div");
  bar.className = "pointer-events-none absolute left-0 top-0 h-0.5 bg-accent";
  bar.style.width = "0%";
  bar.style.opacity = "0";
  hook.el.appendChild(bar);
  hook._progressBar = bar;
  return bar;
};

const stopProgress = (hook: BuildLogHook): void => {
  if (hook._progressRaf) cancelAnimationFrame(hook._progressRaf);
  hook._progressRaf = 0;
};

const syncProgress = (hook: BuildLogHook): void => {
  try {
    const bar = ensureProgressBar(hook);
    const estimatedMs = Number(hook.el.dataset["estimatedMs"] ?? "0");
    const startedAt = hook.el.dataset["buildStartedAt"];
    const isStreaming = hook.el.dataset["streaming"] === "true";

    if (
      !isStreaming ||
      !startedAt ||
      !Number.isFinite(estimatedMs) ||
      estimatedMs <= 0
    ) {
      stopProgress(hook);
      bar.style.width = "0%";
      bar.style.opacity = "0";
      return;
    }

    const startedMs = Date.parse(`${startedAt}Z`);
    if (!Number.isFinite(startedMs)) {
      stopProgress(hook);
      bar.style.width = "0%";
      bar.style.opacity = "0";
      return;
    }

    const tick = () => {
      const elapsed = Date.now() - startedMs;
      const pct = Math.max(0, Math.min(100, (elapsed / estimatedMs) * 100));
      bar.style.width = `${pct}%`;
      bar.style.opacity = "1";
      hook._progressRaf = requestAnimationFrame(tick);
    };

    stopProgress(hook);
    tick();
  } catch (error) {
    stopProgress(hook);
    console.error("[BuildLog] progress sync failed", error);
  }
};

// Fullscreen via a class on <body> (outside the LiveView root) plus a CSS
// rule that promotes #build-log-region to a fixed overlay. Nothing in the
// LV-managed DOM moves, so morphdom's id-keyed matching has no element to
// "move back" mid-stream — no MutationObserver, no reparent bookkeeping.
// Nerd-font glyphs for the toggle button. Open = enter-fullscreen,
// shown when not fullscreened; close = exit-fullscreen, shown while
// the overlay is active.
const FULLSCREEN_OPEN_GLYPH = "\u{F004C}";
const FULLSCREEN_CLOSE_GLYPH = "\u{F0044}";

const setFullscreenGlyph = (glyph: string): void => {
  // The fullscreen toggle button's id is the camelCase wire form emitted
  // by Ids.build_log_fullscreen() (= "buildLogFullscreen"). The hyphenated
  // illuminate form ("build-log-fullscreen") is the body CSS class added
  // on `enterFullscreen`, not the element id.
  const fsBtn = document.getElementById(validCiElementId("buildLogFullscreen"));
  const span = fsBtn?.querySelector<HTMLElement>(".nf-glyph");
  if (span) span.textContent = glyph;
};

const enterFullscreen = (hook: BuildLogHook): void => {
  if (hook._fullscreenActive) return;
  document.body.classList.add("build-log-fullscreen");
  hook._fullscreenActive = true;
  setFullscreenGlyph(FULLSCREEN_CLOSE_GLYPH);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") exitFullscreen(hook);
  };
  document.addEventListener("keydown", onKey);
  hook._escHandler = onKey;

  // CodeMirror needs to remeasure once the container's box changes.
  requestAnimationFrame(() => {
    const cm = hook.el.querySelector<
      HTMLElement & { cmView?: { view?: { requestMeasure(): void } } }
    >(".cm-editor");
    cm?.cmView?.view?.requestMeasure();
  });
};

const exitFullscreen = (hook: BuildLogHook): void => {
  if (!hook._fullscreenActive) return;
  document.body.classList.remove("build-log-fullscreen");
  hook._fullscreenActive = false;
  setFullscreenGlyph(FULLSCREEN_OPEN_GLYPH);

  if (hook._escHandler) {
    document.removeEventListener("keydown", hook._escHandler);
    delete hook._escHandler;
  }

  requestAnimationFrame(() => {
    const cm = hook.el.querySelector<
      HTMLElement & { cmView?: { view?: { requestMeasure(): void } } }
    >(".cm-editor");
    cm?.cmView?.view?.requestMeasure();
  });
};

export const BuildLog = {
  mounted(this: BuildLogHook) {
    initBuildLogViewer(this.el);

    this["_pendingLines"] = [] as string[];
    this["_rafId"] = 0;
    syncProgress(this);

    // Fullscreen button
    const fsBtn = document.getElementById(
      validCiElementId("buildLogFullscreen"),
    );
    if (fsBtn) {
      fsBtn.addEventListener("click", () => {
        if (this._fullscreenActive) exitFullscreen(this);
        else enterFullscreen(this);
      });
    }

    // "Jump to bottom" button — hidden when already at bottom
    const jumpBtn = document.getElementById(
      validCiElementId("buildLogJumpBottom"),
    );
    if (jumpBtn) {
      jumpBtn.addEventListener("click", () => {
        scrollBuildLogToEnd(this.el);
        jumpBtn.classList.add("hidden");
      });
    }

    const updateJumpBtn = () => {
      if (!jumpBtn) return;
      const scroller = this.el.querySelector(".cm-scroller");
      if (!scroller) return;
      const atBottom =
        scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 20;
      if (atBottom) {
        jumpBtn.classList.add("hidden");
      } else {
        jumpBtn.classList.remove("hidden");
      }
    };

    // Watch scroll events on the CodeMirror scroll DOM
    const cmRoot = this.el.querySelector(".cm-editor");
    const scrollDom = cmRoot?.querySelector(".cm-scroller");
    if (scrollDom) {
      scrollDom.addEventListener("scroll", updateJumpBtn);
      this["_scrollDom"] = scrollDom;
      this["_scrollHandler"] = updateJumpBtn;
    }

    const flush = () => {
      const lines = this["_pendingLines"] as string[];
      if (lines.length === 0) return;
      this["_pendingLines"] = [];
      appendBuildLogText(this.el, lines.join("\n") + "\n");
      if (isBuildLogAutoScroll(this.el)) scrollBuildLogToEnd(this.el);
      updateJumpBtn();
    };

    // Streaming: individual lines batched per animation frame
    this.handleEvent("buildLogLine", (raw: unknown) => {
      const { lines } = raw as { lines: string[] };
      for (const line of lines) (this["_pendingLines"] as string[]).push(line);
      if (!(this["_rafId"] as number)) {
        this["_rafId"] = requestAnimationFrame(() => {
          this["_rafId"] = 0;
          flush();
        });
      }
    });

    // Full content load (persisted log for completed jobs)
    this.handleEvent("buildLogLoad", (raw: unknown) => {
      const { content } = raw as { content: string };
      setBuildLogContent(this.el, content);
      updateJumpBtn();
    });

    // Builder execve'd — update progress bar start time
    this.handleEvent("builderStarted", (raw: unknown) => {
      const { job_id, timestamp } = raw as {
        job_id: number;
        timestamp: string;
      };
      if (this.el instanceof HTMLElement) {
        const currentJobId = this.el.dataset["jobId"];
        if (currentJobId && currentJobId !== String(job_id)) return;
        this.el.dataset["buildStartedAt"] = timestamp;
        syncProgress(this);
      }
    });

    // Clear (job switch)
    this.handleEvent("buildLogClear", () => {
      setBuildLogContent(this.el, "");
      this["_pendingLines"] = [];
      if (jumpBtn) jumpBtn.classList.add("hidden");
    });
  },

  updated(this: BuildLogHook) {
    syncProgress(this);
  },

  destroyed(this: BuildLogHook) {
    if (this._fullscreenActive) exitFullscreen(this);
    const rafId = this["_rafId"] as number;
    if (rafId) cancelAnimationFrame(rafId);
    stopProgress(this);
    const scrollDom = this["_scrollDom"];
    const handler = this["_scrollHandler"] as (() => void) | undefined;
    if (scrollDom && handler) scrollDom.removeEventListener("scroll", handler);
    destroyBuildLogViewer(this.el);
  },
};
